import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col, Card, Alert, Button } from "react-bootstrap";
import ClipLoader from "react-spinners/ClipLoader";
import { override } from "components/spinner/spinnerOverride";
import queryString from "query-string";
import { usePrevious } from "lib/hooks";
import { loginUserAction } from "store/actions/actionCreators";
import { isBasicVerified, isTwoFactorVerified } from "services";
import { getTheme } from "lib/theme";
import { ThemeLogo } from "components/pages/auth/ThemeLogo";
import { Navbar } from "@avlino-inc/alenza-react";
import genStyles from "styles/General.module.scss";
import styles from "./Auth.module.scss";
import {
  LoginUserRequestType,
  LoginDataDeserializerType,
  ErrorType,
} from "types";
import {
  getLoginData,
  getLoginError,
  getGlobalMessageReducerResponse,
} from "store/selectors";
import { handleGlobalMessageAlert } from "util/reactSnippets";
import * as routes from "routes";

type PossibleUrlParams = {
  redirect?: string;
};

const AuthLoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [existingAuthChecked, setExistingAuthChecked] = useState<Boolean>(
    false
  );
  const [username, setUsername] = useState<string>();
  const [password, setPassword] = useState<string>();
  // eslint-disable-next-line
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [displayError, setDisplayError] = useState<JSX.Element>();
  // eslint-disable-next-line
  const [loginFailed, setLoginFailed] = useState<boolean>(false);
  // eslint-disable-next-line
  const [updatingCredentials, setUpdatingCredentials] = useState<boolean>(
    false
  );
  const [theme, setTheme] = useState<string | undefined>();
  const [urlParams] = useState<PossibleUrlParams>(
    queryString.parse(location.search)
  );

  // From store
  const loginData: LoginDataDeserializerType | undefined = useSelector(
    getLoginData
  );
  const loginError: ErrorType | undefined = useSelector(getLoginError);
  const globalMessage: string | undefined = useSelector((state) =>
    getGlobalMessageReducerResponse(state)
  );

  // Previous
  const prevUsername: string | undefined = usePrevious(username);
  const prevPassword: string | undefined = usePrevious(password);

  useEffect(() => {
    log.info("AuthLoginPage useEffect()...");
    setTheme(getTheme());
    log.info(`Theme set as: ${theme}`);
  }, []);

    /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    log.info("URL PARAMS: " + JSON.stringify(urlParams));
    // If client already has two-factored JWT in storage, auto-route to dashboard
    if (theme && !existingAuthChecked) {
      setExistingAuthChecked(true);
      isBasicVerified().then((isLoggedIn: boolean) => {
        if (isLoggedIn) {
          isTwoFactorVerified().then((isFullyVerified: boolean) => {
            let path: string;
            if (isFullyVerified) {
              log.info(`User is fully verified!`);
              if (urlParams && urlParams.redirect) {
                log.info(`Redirect query provided: ${urlParams.redirect}`);
                path = urlParams.redirect;
              } else {
                log.info("No redirect query param provided.");
                path =
                  theme === "aicon"
                    ? routes.AICON_DUMMY_ROUTE
                    : routes.DASHBOARD_MAIN;
              }
              log.info(`Setting path as : ${path}`);
            } else {
              // If User is already registered with Authy, it will re-route
              // to AuthVerificationPage
              path = routes.TWOFACTOR_REGISTER;
              if (urlParams && urlParams.redirect) {
                path = `${path}?redirect=${urlParams.redirect}`;
              }
            }
            log.info(`Pushing to : ${path}`);
            history.push(path);
          });
        } else {
          log.info("Stay on login page.");
        }
      });
    }
  }, [existingAuthChecked, history, location.search, urlParams, theme]);

  useEffect(() => {
    log.info("-- Checking for successful login...");
    if (loginData) {
      // successful login
      log.info("Good login! Setting 'user_session' token...");
      localStorage.setItem("user_session", loginData.token);
      ReactGA.event({
        category: "Login",
        action: `Successful Login from User: ${username}`,
      });
      if (loginData.require_twofactor) {
        // two-factor authentication is ON
        if (loginData.authy_id) {
          // User is already registered with Authy
          if (urlParams && urlParams.redirect) {
            // redirect path has been given in URL
            history.push(
              `${routes.TWOFACTOR_VERIFY}?redirect=${urlParams.redirect}`
            );
          } else {
            history.push(`${routes.TWOFACTOR_VERIFY}`);
          }
        } else if (urlParams && urlParams.redirect) {
          history.push(
            `${routes.TWOFACTOR_REGISTER}?redirect=${urlParams.redirect}`
          );
        } else {
          history.push(`${routes.TWOFACTOR_REGISTER}`);
        }
      } else {
        // two-factor authentication is OFF
        theme === "aicon"
          ? history.push(routes.AICON_DUMMY_ROUTE)
          : history.push(routes.DASHBOARD_MAIN);
      }
    } else if (loginError) {
      setShowError(true);
      setLoginFailed(true);
      setLoggingIn(false);
      setDisplayError(
        <Alert variant="danger" onClose={() => setShowError(false)}>
          {loginError.message}
        </Alert>
      );
    }
  }, [loginError, history, loginData, urlParams]);

  useEffect(() => {
    const usernameChanged = prevUsername && username !== prevUsername;
    const passwordChanged = prevPassword && password !== prevPassword;
    log.info(`usernameChanged? ${usernameChanged}`);
    log.info(`passwordChanged? ${passwordChanged}`);
    if (usernameChanged || passwordChanged) {
      setShowError(false);
      setUpdatingCredentials(true);
    }
  }, [
    username,
    password,
    prevUsername,
    prevPassword,
    setShowError,
    setUpdatingCredentials,
  ]);

  const onHandleLoginAttempt = (
    event: any,
    username: string | undefined,
    password: string | undefined
  ) => {
    event.preventDefault();
    if (username && password) {
      const request: LoginUserRequestType = {
        body: { username, password },
      };
      setLoggingIn(true);
      ReactGA.event({
        category: "Login",
        action: `Login Attempt from User: ${username}`,
      });
      log.info("-- Dispatching loginUserAction()...");
      dispatch(loginUserAction(request));
    }
  };

  const onClickUserCard = useCallback(() => history.push(routes.LOGIN), []);

  return (
    <div id="AuthLoginPage" className={styles["auth-page"]}>
      <Navbar
					showLogo
					logoUrl='/'
          productDefinition = {
            {
              productName:'Alenza'
            }
          }
					productNameUrl='/'
					dropdown={undefined}
					notificationItems={undefined}
          onClickUserCard={onClickUserCard}
          email={""}
      />
      {handleGlobalMessageAlert(globalMessage, true)}
      <Container className={styles["auth-page__container"]} fluid={true}>
        <Row className={styles["auth-page__container__row"]}>
          <Col md={12} className={styles["auth-page__container__row__content"]}>
            <Card
              className={
                styles["auth-page__container__row__content__auth-card"]
              }
            >
              <Card.Body className="text-center">
                <div>
                  <ThemeLogo theme={theme} />
                </div>
                <div>
                  <h3>Log In</h3>
                  {showError && loginError ? displayError : null}
                  <form name="login-form" autoComplete="new-password">
                    <input
                      type="text"
                      name="username"
                      placeholder="Username/Email"
                      className={[genStyles["alenza__input"], "w-75"].join(" ")}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setUsername(e.target.value);
                      }}
                      autoFocus
                    />
                    <input
                      type="password"
                      name="password"
                      placeholder="Password"
                      className={[genStyles["alenza__input"], "w-75"].join(" ")}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setPassword(e.target.value);
                      }}
                      autoComplete={"new-password"}
                    />
                    <Button
                      type="submit"
                      className={[
                        genStyles["alenza__btn-primary"],
                        "w-75",
                      ].join(" ")}
                      disabled={!username || !password || loggingIn}
                      onClick={(e: any) => {
                        onHandleLoginAttempt(e, username, password);
                      }}
                    >
                      {loggingIn ? (
                        <ClipLoader
                          color={"#ffffff"}
                          css={override}
                          size={14}
                          loading={loggingIn}
                        />
                      ) : (
                        <span>Log In</span>
                      )}
                    </Button>
                  </form>
                </div>
              </Card.Body>
              <Card.Footer>
                <div
                  className={genStyles["link"]}
                  onClick={() => history.push(routes.FORGOTTEN_PASSWORD)}
                >
                  Forgot Password?
                </div>
                <div
                  className={genStyles["link"]}
                  onClick={() => history.push(routes.RESEND_VERIFICICATION)}
                >
                  Resend Verification Email
                </div>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AuthLoginPage;
